import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Button } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCommissionProgressById } from "../../api/commissionProgressApi";
import { createLog } from "../../api/logApi";
import { convertDateTimeToDateString } from "../../utils/datetimeUtils";
import { useUser } from "../../context/userContext";
import LogViewer from "../common/logViewer";

const PP = "'Poppins', sans-serif";

export default function CommissionDetail() {
    const { commProgressId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchData = async () => {
        try {
            const res = await getCommissionProgressById(commProgressId);
            setData(res.data);
        } catch (e) {
            if (e.response?.status === 403) {
                setForbidden(true);
                setTimeout(() => navigate('/dashboard'), 3000);
            }
            console.error("Failed to load commission detail:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [commProgressId]);

    const handleSubmitRemark = async () => {
        if (!remarks.trim()) return;
        try {
            await createLog({
                module: "Commission Progress",
                recordId: parseInt(commProgressId),
                action: "Remark",
                remarks: remarks.trim(),
                createdBy: user?.username ?? ""
            });
            setRemarks("");
            setRefreshKey(prev => prev + 1);
        } catch (e) {
            console.error("Failed to submit remark:", e);
            alert("Failed to submit remark.");
        }
    };

    if (loading) return <Typography sx={{ p: 3, fontFamily: PP }}>Loading...</Typography>;
    if (forbidden) return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
            <Typography sx={{ fontFamily: PP, fontSize: '1.3rem', fontWeight: 600, color: '#64748B' }}>Uh Oh! Wrong Access.</Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.95rem', color: '#94A3B8', mt: 1 }}>See you again another place.</Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#CBD5E1', mt: 2 }}>Redirecting to dashboard...</Typography>
        </Box>
    );
    if (!data) return (
        <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: PP, color: '#EF4444' }}>Failed to load commission detail.</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2, fontFamily: PP, textTransform: 'none' }}>Go Back</Button>
        </Box>
    );

    const cd = data.commissionProgressDto;
    const sd = data.salesDetailsForCommissionProgress;
    const commPay = parseFloat(cd?.commPay) || 0;
    const firstPay = parseFloat(cd?.firstPayment) || 0;
    const secondPay = parseFloat(cd?.secondPayment) || 0;
    const repayments = [cd?.repayment, cd?.repayment2, cd?.repayment3, cd?.repayment4, cd?.repayment5].filter(Boolean);
    const repayDates = [cd?.repaymentOn, cd?.repayment2On, cd?.repayment3On, cd?.repayment4On, cd?.repayment5On];
    const allRepaySum = repayments.reduce((sum, r) => sum + (parseFloat(r) || 0), 0);
    const totalPaid = firstPay + secondPay + allRepaySum;
    const remaining = Math.round((commPay - totalPaid) * 100) / 100;
    const percentage = commPay > 0 ? Math.round((totalPaid / commPay) * 100 * 100) / 100 : 0;
    const commRate = sd?.finalPrice > 0 ? Math.round((commPay / sd.finalPrice) * 100 * 100) / 100 : 0;

    const sectionTitle = (text) => (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 2.5, mb: 1, pb: 0.5, borderBottom: '1px solid #E2E8F0', fontFamily: PP }}>
            {text}
        </Typography>
    );

    const payRow = (label, amount, date) => {
        if (!amount && !date) return null;
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #F1F5F9' }}>
                <Typography sx={{ fontSize: '0.8rem', fontFamily: PP, color: '#374151' }}>{label}</Typography>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.8rem', fontFamily: PP, fontWeight: 600, color: '#0F172A' }}>
                        RM {parseFloat(amount).toFixed(2)}
                    </Typography>
                    {date && <Typography sx={{ fontSize: '0.68rem', fontFamily: PP, color: '#94A3B8' }}>{convertDateTimeToDateString(date)}</Typography>}
                </Box>
            </Box>
        );
    };

    const repayLabels = ['3rd', '4th', '5th', '6th', '7th'];

    return (
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                {cd?.commPayType} — Sale #{sd?.salesId}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                {sd?.customerName} · {cd?.paymentToName}
            </Typography>

            <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                    {[
                        { label: 'Total Commission', value: `RM ${commPay.toFixed(2)}`, bg: '#F8FAFC', color: '#0F172A' },
                        { label: 'Paid', value: `RM ${totalPaid.toFixed(2)}`, bg: '#ECFDF5', color: '#10B981' },
                        { label: 'Remaining', value: `RM ${remaining.toFixed(2)}`, bg: '#FFF7ED', color: '#F59E0B' },
                        { label: 'Progress', value: `${percentage}%`, bg: '#EFF6FF', color: '#1A8CFF' },
                    ].map(card => (
                        <Box key={card.label} sx={{ background: card.bg, borderRadius: '10px', padding: '10px 14px' }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94A3B8', mb: 0.5, fontFamily: PP }}>{card.label}</Typography>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: card.color, fontFamily: PP }}>{card.value}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Commission Calculation */}
                <Box sx={{ background: '#F8FAFC', borderRadius: '10px', padding: '12px 16px', mb: 2, border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: PP }}>Commission Calculation</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>Type: <strong>{cd?.commPayType ?? '—'}</strong></Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>Payment To: <strong>{cd?.paymentToName ?? '—'}</strong></Typography>
                    </Box>
                    <Box sx={{ borderTop: '1px dashed #E2E8F0', pt: 0.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: PP }}>
                            Final Price: <strong>RM {(sd?.finalPrice || 0).toLocaleString()}</strong>
                            {' × '} Rate: <strong>{commRate}%</strong>
                            {' = '} <strong style={{ color: '#1A8CFF' }}>RM {commPay.toFixed(2)}</strong>
                        </Typography>
                    </Box>
                </Box>

                {/* Sales Info */}
                {sectionTitle('Sales Information')}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    {[
                        ['Agent', sd?.agentName],
                        ['Customer', sd?.customerName],
                        ['Co-Agent (Offline)', sd?.coAgentName],
                        ['Deposit Date', sd?.depositOn ? convertDateTimeToDateString(sd.depositOn) : '—'],
                    ].filter(([, v]) => v).map(([label, value]) => (
                        <Box key={label}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', fontFamily: PP }}>{label}</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontFamily: PP }}>{value}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Payment History */}
                {sectionTitle('Payment History')}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {payRow('1st Pay', cd?.firstPayment, cd?.firstPaymentOn)}
                    {payRow('2nd Pay', cd?.secondPayment, cd?.secondPaymentOn)}
                    {repayments.map((amt, idx) => payRow(`${repayLabels[idx]} Pay`, amt, repayDates[idx]))}
                    {!firstPay && !secondPay && allRepaySum === 0 && (
                        <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontFamily: PP, py: 1 }}>No payments recorded yet.</Typography>
                    )}
                </Box>

                {/* Remarks & Changes History */}
                <Box sx={{ mt: 2 }}>
                    <LogViewer key={refreshKey} module="Commission Progress" recordId={data.commProgressId} remarks={remarks} setRemarks={setRemarks} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button onClick={handleSubmitRemark} variant="contained" disabled={!remarks.trim()}
                            sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', fontSize: '0.8rem' }}>
                            Submit Remark
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
